import { Button, Checkbox, Tag } from '@ads/components-react';
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

import type { ConsolidationSummary } from '../../../utils/deriveConsolidation';
import { getParcelDiscount, getParcelNetValue } from '../../../utils/deriveConsolidation';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { ContractStatusBadge } from '../../atoms/ContractStatusBadge';

export interface ConsolidationTableProps {
  /** Demonstrativo do ano — linhas e agregados do rodapé. */
  readonly summary: ConsolidationSummary;
  /** Ano selecionado, usado no título e no rodapé. */
  readonly year: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  /**
   * Exportação do demonstrativo. Recebe as parcelas escolhidas, ou **todas** as
   * do ano quando nada está marcado — "sem seleção" significa "o ano inteiro",
   * não "nada", senão o botão nasceria inerte na chegada.
   */
  readonly onDownloadPdf: (parcels: readonly IndividualPaymentParcelViewModel[]) => void;
}

/**
 * Célula de dinheiro alinhada à direita.
 *
 * O `<td>` do `DataTable` é `text-left` fixo, então o alinhamento tem de vir do
 * conteúdo. `block` para o `text-right` ter largura sobre a qual agir.
 */
const MONEY_CELL = 'block text-right tabular-nums';

/**
 * Tabela consolidada de um ano — as parcelas do plano que vencem no período,
 * em ordem crescente de vencimento.
 *
 * Construída sobre o `DataTable` compartilhado, como as outras tabelas planas do
 * projeto. As tabelas da feature que montam grid CSS na mão (`ParcelsTable`,
 * `IndividualPaymentTable`) fazem isso por causa de linha expansível e subtabela,
 * que aqui não existem — esta é uma lista plana, e reusar o `DataTable` mantém
 * altura de linha, padding, skeleton e empty state iguais aos do resto do app.
 *
 * Somente leitura, e a `Tag` no cabeçalho diz isso: sem paginação (o recorte
 * anual já limita o volume), sem menu de operações e sem linha expansível. A
 * seleção existe só para recortar a exportação — não muda dado nenhum. Quem
 * precisa operar a parcela vai à tela de parcelas, que é onde as 13 operações
 * vivem.
 *
 * Segue o frame `ZYlnC` de `design/payment.pen`, com a exportação movida para o
 * rodapé, junto da seleção que a alimenta.
 */
export function ConsolidationTable({
  summary,
  year,
  isLoading,
  isError,
  onRetry,
  onDownloadPdf,
}: ConsolidationTableProps) {
  const { t } = useTranslation('individualPayment');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set());

  /**
   * Seleção efetiva: o cruzamento entre o que foi marcado e o que a tabela
   * exibe agora. Um refetch pode ter removido uma parcela marcada, e contá-la
   * faria o rodapé anunciar mais linhas do que existem na tela.
   */
  const selectedParcels = useMemo(
    () => summary.rows.filter(parcel => parcel.id != null && selectedIds.has(parcel.id)),
    [summary.rows, selectedIds],
  );

  /** Há linhas para selecionar e exportar — falso durante carga, erro e vazio. */
  const hasRows = !isLoading && !isError && summary.rows.length > 0;

  const areAllSelected = summary.rows.length > 0 && selectedParcels.length === summary.rows.length;
  const isPartial = selectedParcels.length > 0 && !areAllSelected;

  // `useCallback` nas duas para que a lista de colunas memoizada possa declarar
  // as dependências de verdade, em vez de suprimir a regra de exaustividade.
  const toggle = useCallback((parcelId: number) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(parcelId)) next.delete(parcelId);
      else next.add(parcelId);
      return next;
    });
  }, []);

  // Parcial conta como "marcado" para o clique: o gesto esperado de um
  // indeterminado é completar a seleção, não zerá-la.
  const toggleAll = useCallback(() => {
    setSelectedIds(
      areAllSelected
        ? new Set()
        : new Set(summary.rows.map(parcel => parcel.id).filter((id): id is number => id != null)),
    );
  }, [areAllSelected, summary.rows]);

  const columns = useMemo<ColumnDef<IndividualPaymentParcelViewModel, unknown>[]>(
    () => [
      {
        // Sem `size`: ele só vale com `tableFixed`, que a tabela não usa — a
        // coluna encolhe sozinha para o checkbox.
        id: 'select',
        header: () => (
          // `checked` precisa ser `'indeterminate'` na seleção parcial, não
          // `false`: o `Checkbox` do ADS escolhe o ícone de traço pela prop
          // `indeterminate`, mas quem decide se o indicador chega a renderizar é
          // o `Indicator` do Radix, que some com `checked={false}`. Só com a
          // prop do ADS a caixa apareceria vazia durante a seleção parcial.
          <Checkbox
            size="sm"
            aria-label={t('consolidation.table.selectAll')}
            checked={isPartial ? 'indeterminate' : areAllSelected}
            indeterminate={isPartial}
            onCheckedChange={() => toggleAll()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="sm"
            aria-label={t('consolidation.table.selectRow', {
              parcel: row.original.numberParcel ?? EMPTY_VALUE,
            })}
            checked={row.original.id != null && selectedIds.has(row.original.id)}
            onCheckedChange={() => toggle(row.original.id ?? 0)}
          />
        ),
      },
      {
        // "Tipo" é o `transferType`, que vem pronto como texto na resposta —
        // mesma coluna da `ParcelsTable`. Ocupa o lugar do antigo "X de Y", que
        // cruzava dois endpoints (`numberParcel` da parcela × `numberParcels` do
        // header) e anunciava totais impossíveis, como "3 de 1", quando o header
        // discordava das parcelas. A ordem das linhas já dá a sequência.
        id: 'type',
        header: () => t('consolidation.table.columns.type'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {row.original.transferType ?? EMPTY_VALUE}
          </span>
        ),
      },
      {
        id: 'dueDate',
        header: () => t('consolidation.table.columns.dueDate'),
        cell: ({ row }) => (
          <span className="tabular-nums">{formatContractDate(row.original.expirationDate)}</span>
        ),
      },
      {
        id: 'grossValue',
        header: () => (
          <span className="block text-right">{t('consolidation.table.columns.grossValue')}</span>
        ),
        cell: ({ row }) => <span className={MONEY_CELL}>{formatCurrency(row.original.total)}</span>,
      },
      {
        id: 'discount',
        header: () => (
          <span className="block text-right">{t('consolidation.table.columns.discount')}</span>
        ),
        cell: ({ row }) => {
          const discount = getParcelDiscount(row.original);
          // Desconto zero fica esmaecido: a coluna é quase toda zeros no caso
          // comum, e o contraste cheio competiria com os valores que importam.
          return (
            <span className={`${MONEY_CELL} ${discount > 0 ? '' : 'text-ctx-content-placeholder'}`}>
              {formatCurrency(discount)}
            </span>
          );
        },
      },
      {
        id: 'netValue',
        header: () => (
          <span className="block text-right">{t('consolidation.table.columns.netValue')}</span>
        ),
        cell: ({ row }) => (
          <span className={`${MONEY_CELL} !font-semibold text-ctx-content-title`}>
            {formatCurrency(getParcelNetValue(row.original))}
          </span>
        ),
      },
      {
        id: 'paidAt',
        header: () => t('consolidation.table.columns.paidAt'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.paymentDate ? formatContractDate(row.original.paymentDate) : EMPTY_VALUE}
          </span>
        ),
      },
      {
        id: 'paidValue',
        header: () => (
          <span className="block text-right">{t('consolidation.table.columns.paidValue')}</span>
        ),
        cell: ({ row }) => (
          <span className={`${MONEY_CELL} !font-semibold text-ctx-content-title`}>
            {formatCurrency(row.original.totalPaid)}
          </span>
        ),
      },
      {
        id: 'status',
        header: () => t('consolidation.table.columns.status'),
        cell: ({ row }) =>
          row.original.statusText ? (
            <ContractStatusBadge status={row.original.statusText} />
          ) : (
            <span className="text-ctx-content-placeholder">{EMPTY_VALUE}</span>
          ),
      },
    ],
    [t, selectedIds, areAllSelected, isPartial, toggle, toggleAll],
  );

  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-micro">
      <div className="flex min-w-none flex-col gap-quark">
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {t('consolidation.table.count', { count: summary.total, year })}
          {summary.paid + summary.open > 0 &&
            `  ·  ${t('consolidation.table.breakdown', {
              paid: summary.paid,
              open: summary.open,
            })}`}
        </span>
        <span className="ts-subtitle-xxs tabular-nums text-ctx-content-base">
          {t('consolidation.table.totals', {
            expected: formatCurrency(summary.expectedValue),
            paid: formatCurrency(summary.paidValue),
          })}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-micro">
        {/* A contagem da seleção fica ao lado do botão, não junto dos totais do
            ano: é ela que explica sobre o que o botão vai agir. */}
        {selectedParcels.length > 0 && (
          <span className="ts-caption-xxs text-ctx-content-placeholder">
            {t('consolidation.table.selected', { count: selectedParcels.length })}
          </span>
        )}
        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={ArrowDownTrayIcon}
          onClick={() => onDownloadPdf(selectedParcels.length > 0 ? selectedParcels : summary.rows)}
        >
          {selectedParcels.length > 0
            ? t('consolidation.downloadPdfCount', { count: selectedParcels.length })
            : t('consolidation.downloadPdf')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-xxxs">
      <div className="flex flex-wrap items-center justify-between gap-micro">
        <h2 className="ts-heading-xs text-ctx-content-title">
          {t('consolidation.table.title', { year })}
        </h2>
        <Tag
          label={t('consolidation.table.readOnly')}
          size="sm"
          status="default"
          custom={{ text: 'text-nowrap' }}
        />
      </div>

      {/* O `DataTable` não tem estado de erro, só carga e vazio — o bloco abaixo
          cobre esse buraco reusando a casca de card dele, para a falha não
          aparecer solta na página. */}
      {isError ? (
        <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-md">
          <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
            <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
          </span>
          <p className="ts-heading-xs text-ctx-content-title">{t('consolidation.table.error')}</p>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('consolidation.table.errorHint')}
          </p>
          <Button hierarchy="secondary" size="md" onClick={onRetry}>
            {t('consolidation.table.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          data={summary.rows as IndividualPaymentParcelViewModel[]}
          columns={columns}
          isLoading={isLoading}
          totalCount={summary.rows.length}
          skeletonRowCount={8}
          emptyStateMessage={t('consolidation.table.empty')}
          emptyStateDescription={t('consolidation.table.emptyHint')}
          // O rodapé só existe com linhas: repetir "0 parcelas · R$ 0,00" logo
          // abaixo do empty state seria ruído, e a exportação não teria o que
          // exportar. Sem ele a borda inferior do `<tbody>` também some do
          // caminho, em vez de ficar pendurada sobre o padding do card.
          {...(hasRows ? { footer } : {})}
        />
      )}
    </div>
  );
}
